import React, { useCallback, useEffect, useState } from 'react';

import {
    App,
    Button,
    Card,
    Col,
    ColorPicker,
    ColorPickerProps,
    Form,
    GetProp,
    Input,
    notification,
    Row,
    Select,
    UploadFile
} from 'antd';

import { makeCnpj, makeRawCnpj } from 'lib/helpers/Domain.helper';
import { makeFilterOption } from 'lib/helpers/Form.helper';
import { handleServiceError } from 'lib/helpers/ServiceHelper';
import { useAccount } from 'lib/providers/AccountContextProvider';
import { UploadField } from 'lib/UploadField';
import { UrlField } from 'lib/UrlField';
import { updateAccount } from 'services/Account.service';
import { listGroups } from 'services/ZApi.service';
import { Domain } from 'types/Domain.type';
import { WhatsApp } from 'types/WhatsApp';

const filterOption = makeFilterOption();

type Color = Extract<GetProp<ColorPickerProps, 'value'>, string | { cleared: any }>;

type Values = {
    name: string,
    cnpj: string,
    primary_email: string,
    primary_phone: string,
    url: string,
    logo: UploadFile[],
    /** @example '#87e0d1' */
    primary_color: Color | null
    /** @example '#87e0d1' */
    secondary_color: Color | null,
    whatsapp_group_id: string | null
}

export function ClientManagerCard() {
    const [form] = Form.useForm<Values>();
    const [groups, setGroups] = useState<WhatsApp.Group[]>([]);
    const [isFetchingGroups, setIsFetchingGroups] = useState<boolean>(false);
    const [isSavingClientManager, setIsSavingClientManager] = useState<boolean>(false);

    const app = App.useApp();

    const { clientManager, isLoading } = useAccount();

    const defaultURL =
        ['unit', 'plan', 'trial'].includes(clientManager.contract)
            ? 'https://app.gcondo.com.br/gc/'
            : 'https://sindigest.com.br/gc/';

    const fetchGroups = useCallback(async () => {
        setIsFetchingGroups(true);

        const response = await listGroups();

        setIsFetchingGroups(false);

        if (!response.success)
            return handleServiceError(app, response);

        setGroups(response.groups);
    }, [app]);

    const onFinish = async (values: Values) => {
        setIsSavingClientManager(true);

        const logo: string | null = values.logo && values.logo.length > 0
            ? values.logo[0].url || values.logo[0].response || null
            : null;

        const response = await updateAccount({
            logo: logo,
            name: values.name,
            cnpj: makeRawCnpj(values.cnpj as Domain.Cnpj),
            primary_email: values.primary_email,
            primary_phone: values.primary_phone,
            url: values.url,
            primary_color: typeof values.primary_color === 'string'
                ? values.primary_color
                // @ts-ignore
                : values.primary_color?.toHexString(),
            secondary_color: typeof values.secondary_color === 'string'
                ? values.secondary_color
                // @ts-ignore
                : values.secondary_color?.toHexString(),
            whatsapp_group_id: values.whatsapp_group_id
        });

        setIsSavingClientManager(false);

        if (response.success) {
            notification.success({ message: response.message, description: response.description });
            window.location.reload();
        }
        else {
            notification.error({ message: 'Error ao atualizar os dados', description: response.message });
        }


    };

    useEffect(() => {
        if (!clientManager.id)
            return;

        if (clientManager.logo) {
            const logo: UploadFile[] = [{
                uid: clientManager.logo,
                name: clientManager.logo,
                url: clientManager.logo,
                status: 'done'
            }];

            form.setFieldsValue({ logo });
        }

        fetchGroups();

        form.setFieldsValue({
            name: clientManager.name,
            cnpj: clientManager.cnpj ? makeCnpj(clientManager.cnpj) : '',
            primary_email: clientManager.primary_email,
            primary_phone: clientManager.primary_phone,
            url: clientManager.url,
            primary_color: clientManager.primary_color,
            secondary_color: clientManager.secondary_color,
            whatsapp_group_id: clientManager.whatsapp_group_id
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientManager]);

    return (
        <Card title="Informações da empresa" loading={isLoading}>
            <Form
                form={form}
                onFinish={onFinish}
                name="createClientManager"
                layout="vertical"
                autoComplete="off"
            >
                <Row>
                    <Col xs={24} sm={24} md={24} lg={6}>
                        <Form.Item>
                            <UploadField
                                name="logo"
                                type='picture-card'
                                buttonText='Logo'
                                maxCount={1}
                                multiple={false}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={18}>
                        <Row>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="name"
                                    label="Nome"
                                    rules={[{ required: true, message: 'Por favor, digite um nome.' }]}
                                >
                                    <Input maxLength={500} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="cnpj"
                                    label="CNPJ"
                                    rules={[{ required: true, message: 'Por favor, digite um CNPJ.' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="primary_email"
                                    label="E-mail principal"
                                    rules={[
                                        { required: true, message: 'Por favor, digite um e-mail principal.' },
                                        { type: 'email', message: 'Por favor, digite um e-mail válido.' }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="primary_phone"
                                    label="Telefone principal"
                                    rules={[{ required: true, message: 'Por favor, digite um telefone principal.' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="whatsapp_group_id"
                                    label="Grupo de WhatsApp da Empresa"
                                >
                                    <Select
                                        loading={isFetchingGroups}
                                        showSearch
                                        filterOption={filterOption}
                                        options={groups}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={24} lg={24}>
                                <Form.Item>
                                    <UrlField
                                        name="url"
                                        baseUrl={defaultURL}
                                        currentUrl={clientManager.url}
                                        rules={[
                                            {
                                                pattern: /^[a-z0-9-]+$/,
                                                message: 'A URL deve conter apenas letras minúsculas, números e hífens.',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="primary_color"
                                    label="Cor principal"
                                >
                                    <ColorPicker format="hex" showText />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="secondary_color"
                                    label="Cor secundária"
                                >
                                    <ColorPicker format="hex" showText />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row justify="end">
                    <Col>
                        <Button loading={isSavingClientManager} type="primary" htmlType="submit">
                            Atualizar
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};